class HeaderDetailsDisclosure extends HTMLElement {
  #mouseEnterHandler;
  #mouseLeaveHandler;
  #focusHandler;
  #blurHandler;
  #clickHandler;
  #hoverTimer;
  linkElement = null;

  constructor() {
    super();
    this.#mouseEnterHandler = this.onMouseEnter.bind(this);
    this.#mouseLeaveHandler = this.onMouseLeave.bind(this);
    this.#focusHandler = this.onFocus.bind(this);
    this.#blurHandler = this.onBlur.bind(this);
    this.#clickHandler = this.onClick.bind(this);
    this.mainDetailsToggle = null;
    this.linkElement = null;
    this.content = null;
    this.panelAnimations = null;
    this.contentAnimations = null;
  }

  connectedCallback() {
    // Check if THIS element itself has the classes we're looking for
    if (this.classList.contains('header-menu-disclosure') ||
        this.classList.contains('header-localize-disclosure') ||
        this.classList.contains('header-submenu-disclosure')) {
      this.mainDetailsToggle = this;
    } else {
      // Find the new structure elements as children
      this.mainDetailsToggle = this.querySelector('.header-menu-disclosure, .header-localize-disclosure, .header-submenu-disclosure');
    }

    if (!this.mainDetailsToggle) {
      // Fallback for any remaining details elements
      this.mainDetailsToggle = this.querySelector('details');
      if (this.mainDetailsToggle) {
        // Use original behavior for details elements
        this.content = this.mainDetailsToggle.querySelector("summary")?.nextElementSibling;
        if (this.mainDetailsToggle.hasAttribute("open")) {
          this.setAttribute('data-open', 'true');
        }
        this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
      }
      return;
    }

    // New structure handling - find the link or button (they might not have aria-expanded yet)
    this.linkElement = this.mainDetailsToggle.querySelector('nav a, button');

    if (!this.linkElement) return;

    // Initialize aria-expanded if not present
    if (!this.linkElement.hasAttribute('aria-expanded')) {
      this.linkElement.setAttribute('aria-expanded', 'false');
    }

    // Find the dropdown content
    const contentId = this.linkElement.getAttribute('aria-controls');
    this.content = contentId ? document.getElementById(contentId) : this.mainDetailsToggle.querySelector('[aria-hidden]');

    // Set up event listeners based on element type
    if (this.linkElement.tagName === 'A') {
      // For links with dropdowns - hover and focus behavior
      this.mainDetailsToggle.addEventListener("mouseenter", this.#mouseEnterHandler);
      this.mainDetailsToggle.addEventListener("mouseleave", this.#mouseLeaveHandler);

      // Use focusin/focusout which bubble, so we can catch focus on container
      this.mainDetailsToggle.addEventListener("focusin", this.#focusHandler);
      this.mainDetailsToggle.addEventListener("focusout", this.#blurHandler);

      // Prevent navigation when dropdown is open
      this.linkElement.addEventListener("click", this.#clickHandler);
    } else if (this.linkElement.tagName === 'BUTTON') {
      // For buttons (localization) - click behavior
      this.linkElement.addEventListener('click', this.#clickHandler);
    }

    // Initialize state
    if (this.linkElement.getAttribute('aria-expanded') === 'true') {
      this.setAttribute('data-open', 'true');
    }
  }

  disconnectedCallback() {
    this.disableListeners();
  }

  onMouseEnter() {
    if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;

    // Clear any pending close timer
    if (this.#hoverTimer) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = null;
    }

    this.open();
  }

  onMouseLeave() {
    if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;

    // Add small delay to prevent flicker when moving between menu items
    this.#hoverTimer = setTimeout(() => {
      this.close();
    }, 100);
  }

  onFocus(e) {
    if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;

    // Clear any pending close timer
    if (this.#hoverTimer) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = null;
    }

    this.open();
  }

  onBlur() {
    if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;

    // Add small delay to prevent flicker when moving between menu items
    this.#hoverTimer = setTimeout(() => {
      // Check if focus is still within this element
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 100);
  }

  onClick(e) {
    const isOpen = this.getAttribute('data-open') === 'true';
    const linkElement = this.querySelector('a[aria-expanded], button[aria-expanded]');
    if (linkElement && linkElement.tagName === 'A') {
      // For links, prevent navigation when menu is open
      if (isOpen) {
        e.preventDefault();
        this.close();
      }
      // Allow navigation when menu is closed
    } else if (linkElement && linkElement.tagName === 'BUTTON') {
      // For buttons, toggle the menu
      e.preventDefault();
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  open() {
    if (this.getAttribute('data-open') === 'true') return;

    this.setAttribute('data-open', 'true');

    // Find and update ARIA states
    const linkElement = this.querySelector('a[aria-expanded], button[aria-expanded]');
    if (linkElement) {
      linkElement.setAttribute("aria-expanded", "true");
    }

    // Find and show content
    const contentId = linkElement?.getAttribute('aria-controls');
    const content = contentId ? document.getElementById(contentId) : this.querySelector('[aria-hidden]');
    if (content) {
      content.setAttribute("aria-hidden", "false");
      content.classList.remove("hidden");

      // Check if content overflows viewport
      const rect = content.getBoundingClientRect();
      if (rect.x + rect.width > window.innerWidth - 30) {
        content.classList.add("is-left-aligned");
      }
    }

    // Handle mega menu height adjustment
    if (this.hasAttribute("adjust-mega-menu-height")) {
      this.adjustMegaMenuHeight(36);
    }

    // Set body attribute for styling
    document.body.setAttribute("header-menu-open", "");

    // Trigger animations
    this.playAnimations();
  }

  close() {
    if (this.getAttribute('data-open') !== 'true') return;

    this.setAttribute('data-open', 'false');

    // Find and update ARIA states
    const linkElement = this.querySelector('a[aria-expanded], button[aria-expanded]');
    if (linkElement) {
      linkElement.setAttribute("aria-expanded", "false");
    }

    // Find and hide content
    const contentId = linkElement?.getAttribute('aria-controls');
    const content = contentId ? document.getElementById(contentId) : this.querySelector('[aria-hidden]');
    if (content) {
      content.setAttribute("aria-hidden", "true");
      content.classList.remove("is-left-aligned");
    }

    // Handle mega menu height adjustment
    if (this.hasAttribute("adjust-mega-menu-height")) {
      this.resetMegaMenuHeight();
    }

    // Remove body attribute
    document.body.removeAttribute("header-menu-open");

    // Cancel animations
    this.cancelAnimations();
  }

  onToggle() {
    // For backward compatibility with details elements
    if (this.mainDetailsToggle && this.mainDetailsToggle.tagName === 'DETAILS') {
      if (this.mainDetailsToggle.hasAttribute("open")) {
        this.setAttribute('data-open', 'true');
        this.playAnimations();
        document.body.setAttribute("header-menu-open", "");
      } else {
        this.setAttribute('data-open', 'false');
        this.cancelAnimations();
        document.body.removeAttribute("header-menu-open");
      }
    }
  }

  playAnimations() {
    const content = this.querySelector('[aria-hidden="false"]');
    this.panelAnimations || (this.panelAnimations = content ? content.getAnimations() : []);
    this.contentAnimations || (this.contentAnimations = Array.from(this.querySelectorAll(".has-animation")).reduce(((e, t) => [...e, ...t.getAnimations()]), []));

    this.panelAnimations.forEach((e => e.play()));
    this.contentAnimations.forEach((e => e.play()));
  }

  cancelAnimations() {
    if (this.panelAnimations) {
      this.panelAnimations.forEach((e => e.cancel()));
    }
    if (this.contentAnimations) {
      this.contentAnimations.forEach((e => e.cancel()));
    }
  }

  adjustMegaMenuHeight(offset = 0) {
    const header = document.querySelector(".js-header");
    const headerBottom = header && header.getBoundingClientRect().bottom > 0 ? header.getBoundingClientRect().bottom : 0;
    const totalOffset = Math.round(headerBottom + offset);

    const content = this.querySelector('[aria-hidden="false"]');
    if (content && content.offsetHeight > window.innerHeight - totalOffset) {
      content.style.setProperty("--header-elements-height", `${totalOffset}px`);
      content.classList.add("has-height-control");
    }
  }

  resetMegaMenuHeight() {
    const content = this.querySelector('[aria-hidden]');
    if (content) {
      content.classList.remove("has-height-control");
    }
  }

  disableListeners() {
    // Only remove mouse/click listeners, keep focus listeners for keyboard navigation
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.removeEventListener("mouseenter", this.#mouseEnterHandler);
      this.mainDetailsToggle.removeEventListener("mouseleave", this.#mouseLeaveHandler);
      this.mainDetailsToggle.removeEventListener("toggle", this.onToggle);
    }
    const linkElement = this.querySelector('a[aria-expanded], button[aria-expanded]');
    if (linkElement) {
      linkElement.removeEventListener('click', this.#clickHandler);
    }
    if (this.#hoverTimer) {
      clearTimeout(this.#hoverTimer);
    }
  }
}

customElements.define("header-details-disclosure", HeaderDetailsDisclosure);
export default HeaderDetailsDisclosure;
//# sourceMappingURL=header-details-disclosure.js.map